import { useEffect } from 'react';
import { Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import {
  ChartLineUp,
  ChatCircleDots,
  PhoneCall,
  Clock,
} from '@phosphor-icons/react';

interface AgentAnalyticsProps {
  analytics: any;
  onAnalyticsChange: (analytics: any) => void;
  onValidationChange: (isValid: boolean) => void;
}

const AgentAnalytics = ({
  analytics,
  onAnalyticsChange,
  onValidationChange,
}: AgentAnalyticsProps) => {
  useEffect(() => {
    // Analytics configuration is optional, always valid
    onValidationChange(true);
  }, [onValidationChange]);

  const handleToggleMetric = (metric: string) => {
    const currentMetrics = analytics?.enabledMetrics || [];
    const newMetrics = currentMetrics.includes(metric)
      ? currentMetrics.filter((m: string) => m !== metric)
      : [...currentMetrics, metric];

    onAnalyticsChange({
      ...analytics,
      enabledMetrics: newMetrics,
    });
  };

  const metrics = [
    {
      id: 'conversations',
      title: 'Total Conversations',
      description:
        'Track the total number of conversations handled by your agent',
      icon: ChatCircleDots,
      color: '#5A4CE6',
    },
    {
      id: 'calls',
      title: 'Total Calls',
      description: 'Monitor the number of calls received and handled',
      icon: PhoneCall,
      color: '#4CAF50',
    },
    {
      id: 'duration',
      title: 'Average Duration',
      description: 'Measure average conversation or call duration',
      icon: Clock,
      color: '#FFA500',
    },
    {
      id: 'performance',
      title: 'Performance Metrics',
      description:
        'Track response time, resolution rate, and customer satisfaction',
      icon: ChartLineUp,
      color: '#2196F3',
    },
  ];

  const enabledMetrics = analytics?.enabledMetrics || [];

  return (
    <Box sx={{ maxWidth: '768px', margin: '0 auto', py: 3 }}>
      {/* Heading */}
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          color: 'custom.textBlue',
          mb: 1,
        }}
      >
        Configure Analytics
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 400,
          color: 'lightGray.text',
          mb: 3,
        }}
      >
        Choose which metrics you want to track for your agent (optional)
      </Typography>

      {/* Metrics selection */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2,
        }}
      >
        {metrics.map(metric => {
          const Icon = metric.icon;
          const isEnabled = enabledMetrics.includes(metric.id);

          return (
            <Box
              key={metric.id}
              onClick={() => handleToggleMetric(metric.id)}
              sx={{
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: isEnabled ? 'primary.main' : 'lightGray.main',
                borderRadius: '12px',
                p: 2.5,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: isEnabled
                    ? 'primary.main'
                    : 'custom.secondaryBlue',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(90, 76, 230, 0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '10px',
                    bgcolor: `${metric.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={24} color={metric.color} weight="duotone" />
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 500,
                        color: 'custom.textBlue',
                      }}
                    >
                      {metric.title}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isEnabled}
                          onChange={() => handleToggleMetric(metric.id)}
                          size="small"
                          sx={{
                            p: 0,
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                          }}
                        />
                      }
                      label=""
                      sx={{ m: 0, ml: 'auto' }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 400,
                      color: 'lightGray.text',
                      lineHeight: 1.4,
                    }}
                  >
                    {metric.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Info box */}
      <Box
        sx={{
          mt: 4,
          bgcolor: 'custom.sidebarActiveBg',
          borderRadius: '8px',
          p: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: 'primary.main',
            lineHeight: 1.6,
          }}
        >
          <strong>Note:</strong> You can always adjust these analytics settings
          later from your dashboard. All metrics are automatically tracked in
          the background.
        </Typography>
      </Box>
    </Box>
  );
};

export default AgentAnalytics;
